/**
 * Edit risk_matrix_schema (Phase 6) - add/rename/reorder risk items in the property and
 * market groups, and set the comment-required threshold. Same "single source of truth
 * for the prompt and the grid" contract as CheckSchemaAdmin.tsx.
 */
import { useEffect, useState } from 'react';
import { fetchAllRiskSchema } from '../../admin/queries';
import { createRiskSchemaRow, updateRiskSchemaRow, reorderRiskSchema, ApiError } from '../../api';
import type { AdminRiskSchemaRow } from '../../admin/types';
import { Banner, Button, Chip, Spinner, Tile } from '../../components/ui';
import { RiskSection } from '../../components/reviewer/RiskSection';

interface DraftRow extends AdminRiskSchemaRow {
  dirty: boolean;
}

const emptyNewRisk = {
  riskKey: '',
  riskGroup: 'property' as 'property' | 'market',
  label: '',
  description: '',
  commentRequiredAbove: 3,
};

const GROUP_LABEL: Record<'property' | 'market', string> = { property: 'Property risk', market: 'Market risk' };

export function RiskMatrixAdmin() {
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [newRisk, setNewRisk] = useState(emptyNewRisk);

  async function load() {
    setError(null);
    try {
      const risks = await fetchAllRiskSchema();
      setRows(risks.map((r) => ({ ...r, dirty: false })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the risk matrix.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!rows) return <Spinner label="Loading the risk matrix…" />;

  function patchRow(riskKey: string, patch: Partial<DraftRow>) {
    setRows((prev) => (prev ? prev.map((r) => (r.riskKey === riskKey ? { ...r, ...patch, dirty: true } : r)) : prev));
  }

  async function save(row: DraftRow) {
    setBusyKey(row.riskKey);
    setError(null);
    try {
      await updateRiskSchemaRow(row.riskKey, {
        riskGroup: row.riskGroup,
        label: row.label,
        description: row.description,
        displayOrder: row.displayOrder,
        commentRequiredAbove: row.commentRequiredAbove,
        active: row.active,
      });
      setRows((prev) => (prev ? prev.map((r) => (r.riskKey === row.riskKey ? { ...r, dirty: false } : r)) : prev));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that risk.');
    } finally {
      setBusyKey(null);
    }
  }

  async function move(group: DraftRow[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= group.length) return;
    const a = group[index]!;
    const b = group[target]!;
    setBusyKey(a.riskKey);
    setError(null);
    try {
      await reorderRiskSchema([
        { riskKey: a.riskKey, displayOrder: b.displayOrder },
        { riskKey: b.riskKey, displayOrder: a.displayOrder },
      ]);
      setRows((prev) =>
        prev
          ? prev.map((r) => {
              if (r.riskKey === a.riskKey) return { ...r, displayOrder: b.displayOrder };
              if (r.riskKey === b.riskKey) return { ...r, displayOrder: a.displayOrder };
              return r;
            })
          : prev,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reorder those risks.');
    } finally {
      setBusyKey(null);
    }
  }

  async function createRisk() {
    if (!newRisk.riskKey.trim() || !newRisk.label.trim()) {
      setError('risk_key and label are required to add a risk.');
      return;
    }
    setBusyKey('__new__');
    setError(null);
    try {
      await createRiskSchemaRow({
        riskKey: newRisk.riskKey.trim(),
        riskGroup: newRisk.riskGroup,
        label: newRisk.label.trim(),
        description: newRisk.description.trim() || null,
        displayOrder: rows && rows.length > 0 ? Math.max(...rows.map((r) => r.displayOrder)) + 10 : 10,
        commentRequiredAbove: newRisk.commentRequiredAbove,
        active: true,
      });
      setNewRisk(emptyNewRisk);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create that risk.');
    } finally {
      setBusyKey(null);
    }
  }

  const groups: Array<{ group: 'property' | 'market'; items: DraftRow[] }> = (['property', 'market'] as const).map((g) => ({
    group: g,
    items: rows.filter((r) => r.riskGroup === g),
  }));

  const activeSchemaForPreview = rows
    .filter((r) => r.active)
    .map((r) => ({
      riskKey: r.riskKey,
      riskGroup: r.riskGroup,
      label: r.label,
      description: r.description,
      displayOrder: r.displayOrder,
      commentRequiredAbove: r.commentRequiredAbove,
    }));

  return (
    <div className="stack">
      {error ? <Banner tone="error">{error}</Banner> : null}

      {groups.map(({ group, items }) => (
        <Tile key={group} className="admin-card">
          <h3 className="checklist-cat__title">{GROUP_LABEL[group]}</h3>
          <div className="admin-rows">
            {items.map((row, index) => (
              <div className="admin-row" key={row.riskKey}>
                <div className="admin-row__order">
                  <Button variant="ghost" onClick={() => void move(items, index, -1)} disabled={index === 0 || busyKey === row.riskKey}>
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => void move(items, index, 1)}
                    disabled={index === items.length - 1 || busyKey === row.riskKey}
                  >
                    ↓
                  </Button>
                </div>
                <div className="admin-row__fields">
                  <input className="input" value={row.label} onChange={(e) => patchRow(row.riskKey, { label: e.target.value })} placeholder="Label" />
                  <textarea
                    className="input"
                    value={row.description ?? ''}
                    onChange={(e) => patchRow(row.riskKey, { description: e.target.value })}
                    placeholder="Description"
                    rows={2}
                  />
                  <label className="admin-row__toggle">
                    Comment required above
                    <input
                      className="input admin-row__number"
                      type="number"
                      min={0}
                      max={5}
                      value={row.commentRequiredAbove}
                      onChange={(e) => patchRow(row.riskKey, { commentRequiredAbove: Number(e.target.value) })}
                    />
                  </label>
                  <label className="admin-row__toggle">
                    <input type="checkbox" checked={row.active} onChange={(e) => patchRow(row.riskKey, { active: e.target.checked })} />
                    Active
                  </label>
                  <Chip>{row.riskKey}</Chip>
                </div>
                <Button onClick={() => void save(row)} disabled={!row.dirty || busyKey === row.riskKey}>
                  {busyKey === row.riskKey ? 'Saving…' : 'Save'}
                </Button>
              </div>
            ))}
          </div>
        </Tile>
      ))}

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Add a risk</h3>
        <div className="admin-new-form">
          <input
            className="input"
            placeholder="risk_key (e.g. prop.solar_panels)"
            value={newRisk.riskKey}
            onChange={(e) => setNewRisk((n) => ({ ...n, riskKey: e.target.value }))}
          />
          <select
            className="input"
            value={newRisk.riskGroup}
            onChange={(e) => setNewRisk((n) => ({ ...n, riskGroup: e.target.value as 'property' | 'market' }))}
          >
            <option value="property">Property risk</option>
            <option value="market">Market risk</option>
          </select>
          <input className="input" placeholder="Label" value={newRisk.label} onChange={(e) => setNewRisk((n) => ({ ...n, label: e.target.value }))} />
          <input
            className="input"
            placeholder="Description (optional)"
            value={newRisk.description}
            onChange={(e) => setNewRisk((n) => ({ ...n, description: e.target.value }))}
          />
          <input
            className="input admin-row__number"
            type="number"
            min={0}
            max={5}
            value={newRisk.commentRequiredAbove}
            onChange={(e) => setNewRisk((n) => ({ ...n, commentRequiredAbove: Number(e.target.value) }))}
          />
          <Button onClick={() => void createRisk()} disabled={busyKey === '__new__'}>
            {busyKey === '__new__' ? 'Adding…' : 'Add risk'}
          </Button>
        </div>
      </Tile>

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Risk matrix preview</h2>
        </div>
        <RiskSection schema={activeSchemaForPreview} results={[]} checked={false} onJump={() => {}} />
      </section>
    </div>
  );
}
